import React, { useState, useRef, useEffect } from "react";
import { get } from "lodash";
import { IconChevronLeft } from "@tabler/icons-react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { HeatMapPriceStatsData } from "../../models/heatMapModel";
import { priceStatsEndpoint } from "../../services/heatMapService";
import { fetchResponseAdapter } from "../../../../adapters/fetchAdapter";
import useCallApiAndLoad from "../../../../hooks/useCallApiAndLoad";
import ZoneHeader from "./ZoneHeader";
import ZonePriceCard from "./ZonePriceCard";
import ZoneMetrics from "./ZoneMetrics";
import ZoneTrends from "./ZoneTrends";
import ZoneActions from "./ZoneActions";
import ZoneEmptyState from "./ZoneEmptyState";

interface Props {
  polygonId: number | null;
  onClose: () => void;
  navbarHeight?: number;
}

// Contenido compartido entre mobile y web
const ZoneContent: React.FC<{
  displayed: HeatMapPriceStatsData | null;
  isOpen: boolean;
  name: string;
  avgPrice: string;
  minPrice: string;
  maxPrice: string;
  variation: string;
  capitalGain: string;
  graphData: any[];
  scrollMaxH: string;
  onClose: () => void;
}> = ({ displayed, isOpen, name, avgPrice, minPrice, maxPrice, variation, capitalGain, graphData, scrollMaxH, onClose }) => (
  <>
    <div className="px-4 pt-2 md:pt-4 h-[60px]">
      <ZoneHeader name={name} onClose={onClose} />
    </div>
    {displayed ? (
      <>
        <div className="flex-1 overflow-y-auto px-4" style={{ maxHeight: scrollMaxH }}>
          <ZonePriceCard avgPrice={avgPrice} minPrice={minPrice} maxPrice={maxPrice} />
          <ZoneMetrics variation={variation} capitalGain={capitalGain} />
          <ZoneTrends key={`${get(displayed, "id")}-${isOpen}`} data={graphData} />
        </div>
        <div className="px-4 py-3 min-h-[120px]">
          <ZoneActions zoneName={name} />
        </div>
      </>
    ) : (
      <ZoneEmptyState />
    )}
  </>
);

const ZoneDrawer: React.FC<Props> = ({ polygonId, onClose, navbarHeight = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [stats, setStats] = useState<HeatMapPriceStatsData | null>(null);
  const { callEndpoint } = useCallApiAndLoad();
  const isMobile = useMediaQuery("(max-width: 767px)");

  // Retiene los últimos stats para mostrar contenido durante la animación de cierre
  const lastStats = useRef<HeatMapPriceStatsData | null>(null);
  useEffect(() => {
    if (stats) lastStats.current = stats;
  }, [stats]);

  // Abre y carga price-stats cuando hay polígono; cierra cuando no hay
  useEffect(() => {
    if (!polygonId) {
      setIsOpen(false);
      return;
    }
    setIsOpen(true);
    const fetchStats = async () => {
      const response = await callEndpoint(priceStatsEndpoint(polygonId));
      const adapted = fetchResponseAdapter(get(response, "data"));
      if (get(adapted, "code") === 0) setStats(get(adapted, "data"));
    };
    fetchStats();
  }, [polygonId]);

  const displayed = stats ?? lastStats.current;
  const name = get(displayed, "name", "");
  const avgPrice = get(stats, "ppm2", "$0");
  const minPrice = get(stats, "typical_range.min", "$0");
  const maxPrice = get(stats, "typical_range.max", "$0");
  const variation = get(stats, "variation", "—");
  const capitalGain = get(stats, "variation_status", "—");
  const graphData = get(stats, "capital_gains_graph", []);

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  const contentProps = {
    displayed, isOpen, name, avgPrice, minPrice, maxPrice,
    variation, capitalGain, graphData, onClose: handleClose,
  };

  // Mobile: SwipeableDrawer de MUI desde abajo
  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={isOpen}
        onClose={handleClose}
        onOpen={() => setIsOpen(true)}
        disableSwipeToOpen
        swipeAreaWidth={0}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          className: "dynamic",
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: "85dvh",
          },
        }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
        <ZoneContent {...contentProps} scrollMaxH="calc(85dvh - 180px)" />
      </SwipeableDrawer>
    );
  }

  // Web: drawer lateral derecho custom
  return (
    <div
      className={`dynamic fixed z-50 transition-transform duration-300 ease-in-out pointer-events-auto
        right-0 max-w-[400px] w-full
        ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      style={{
        top: navbarHeight + 52,
        ["--drawer-h" as string]: `calc(100dvh - ${navbarHeight + 52}px)`,
        maxHeight: `calc(100dvh - ${navbarHeight + 52}px)`,
      }}
    >
      {/* Pestaña para abrir/cerrar */}
      <button
        onClick={() => setIsOpen(true)}
        className={`absolute left-0 top-1/2 -translate-x-full -translate-y-1/2 bg-white shadow-lg rounded-l-xl px-1.5 py-4 transition-opacity duration-300 ${isOpen ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      >
        <IconChevronLeft size={20} className="text-gray-600" />
      </button>

      <div className="bg-white shadow-2xl rounded-l-2xl">
        <ZoneContent {...contentProps} scrollMaxH={`calc(100dvh - ${navbarHeight + 52}px - 252px)`} />
      </div>
    </div>
  );
};

export default ZoneDrawer;
